import {
  Activity,
  Building2,
  Calendar,
  CheckSquare,
  Cloud,
  DollarSign,
  FileText,
  Flag,
  FolderOpen,
  HelpCircle,
  List,
  Receipt,
  Shield,
  ShoppingCart,
  TrendingUp,
  Users,
} from 'lucide-react';
import React from 'react';
import ActivityFeedWidget from './ActivityFeedWidget';
import BudgetWidget from './BudgetWidget';
import DocumentsWidget from './DocumentsWidget';
import MilestonesWidget from './MilestonesWidget';
import ProjectsListWidget from './ProjectsListWidget';
import SafetyWidget from './SafetyWidget';
import ScheduleWidget from './ScheduleWidget';
import TasksWidget from './TasksWidget';
import TeamWidget from './TeamWidget';
import TotalsWidget from './TotalsWidget';
import WeatherWidget from './WeatherWidget';
import { WidgetType } from './types';

// Widget component registry
export const WIDGET_COMPONENTS: Record<string, React.ComponentType<any>> = {
  // Financial widgets
  totals: TotalsWidget,
  'gross-totals': TotalsWidget,
  invoices: TotalsWidget,
  costs: TotalsWidget,
  'bills-summary': TotalsWidget,
  'invoices-summary': TotalsWidget,
  'purchase-order-summary': TotalsWidget,
  'change-order-summary': TotalsWidget,
  'client-invoices': TotalsWidget,
  budget: BudgetWidget,

  // Project & List widgets
  'projects-list': ProjectsListWidget,
  'invoices-list': ProjectsListWidget,
  'bills-list': ProjectsListWidget,
  'purchase-orders-list': ProjectsListWidget,
  'change-order-list': ProjectsListWidget,
  'recent-files': ProjectsListWidget,
  files: ProjectsListWidget,

  // Task & Work widgets
  todos: TasksWidget,
  tasks: TasksWidget,
  rfis: TasksWidget,
  'daily-logs': TasksWidget,
  timesheets: TasksWidget,
  schedule: ScheduleWidget,

  // Team & Activity widgets
  team: TeamWidget,
  'team-performance': TeamWidget,
  'activity-feed': ActivityFeedWidget,
  'recent-activity': ActivityFeedWidget,

  // Documents & Safety widgets
  documents: DocumentsWidget,
  'document-summary': DocumentsWidget,
  safety: SafetyWidget,
  'safety-incidents': SafetyWidget,

  // Planning widgets
  milestones: MilestonesWidget,
  'upcoming-deadlines': MilestonesWidget,

  // External data widgets
  weather: WeatherWidget,
};

// Widget type definitions with metadata
export const WIDGET_TYPES: WidgetType[] = [
  // Financial Widgets
  {
    id: 'totals',
    name: 'Totals',
    description: 'Revenue, costs, and profit overview',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: DollarSign,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'gross-totals',
    name: 'Gross Totals',
    description: 'Gross revenue and margin analysis',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: TrendingUp,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'invoices',
    name: 'Invoices',
    description: 'Invoice status and amounts',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '1x1',
    icon: Receipt,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'costs',
    name: 'Costs',
    description: 'Cost breakdown and tracking',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '1x1',
    icon: DollarSign,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'bills-summary',
    name: 'Bills Summary',
    description: 'Outstanding and paid bills overview',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: Receipt,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'purchase-order-summary',
    name: 'Purchase Order Summary',
    description: 'PO status and amounts',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: ShoppingCart,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'change-order-summary',
    name: 'Change Order Summary',
    description: 'Change order impacts and status',
    category: 'Financial',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: FileText,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Project Widgets
  {
    id: 'projects-list',
    name: 'Projects List',
    description: 'Active projects with status',
    category: 'Projects',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: Building2,
    supportsOrgScope: true,
    supportsProjectScope: false,
  },
  {
    id: 'invoices-list',
    name: 'Invoices List',
    description: 'Recent invoices and status',
    category: 'Financial',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: List,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'bills-list',
    name: 'Bills List',
    description: 'Recent bills and payments',
    category: 'Financial',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: List,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'purchase-orders-list',
    name: 'Purchase Orders & Subcontracts List',
    description: 'Recent POs and subcontracts',
    category: 'Financial',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: ShoppingCart,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'change-order-list',
    name: 'Change Order List',
    description: 'Recent change orders',
    category: 'Financial',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: FileText,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Tasks & Work Widgets
  {
    id: 'tasks',
    name: 'Tasks',
    description: 'Active tasks and assignments',
    category: 'Tasks & Work',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: CheckSquare,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'todos',
    name: 'Todos',
    description: 'Personal and team to-dos',
    category: 'Tasks & Work',
    sizes: ['1x1', '2x1'],
    defaultSize: '2x1',
    icon: CheckSquare,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'rfis',
    name: 'RFIs',
    description: 'Requests for information',
    category: 'Tasks & Work',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: HelpCircle,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'daily-logs',
    name: 'Daily Logs',
    description: 'Recent daily activity logs',
    category: 'Tasks & Work',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: FileText,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'timesheets',
    name: 'Timesheets',
    description: 'Time tracking and hours',
    category: 'Tasks & Work',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: Users,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Documents & Files Widgets
  {
    id: 'recent-files',
    name: 'Recent Files',
    description: 'Recently uploaded files',
    category: 'Documents & Files',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: FolderOpen,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'files',
    name: 'Files',
    description: 'File organization and access',
    category: 'Documents & Files',
    sizes: ['2x1', '2x2'],
    defaultSize: '2x1',
    icon: FolderOpen,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'documents',
    name: 'Documents Summary',
    description: 'Document upload and review status',
    category: 'Documents & Files',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: FileText,
    supportsOrgScope: false,
    supportsProjectScope: true,
  },

  // Budget & Schedule Widgets
  {
    id: 'budget',
    name: 'Budget Overview',
    description: 'Budget utilization and variance tracking',
    category: 'Financial',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: DollarSign,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'schedule',
    name: 'Schedule Progress',
    description: 'Project schedule and task completion',
    category: 'Tasks & Work',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: Calendar,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Team & Activity Widgets
  {
    id: 'team',
    name: 'Team Overview',
    description: 'Team members and performance metrics',
    category: 'Reports & Analytics',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: Users,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },
  {
    id: 'activity-feed',
    name: 'Activity Feed',
    description: 'Recent team activity and updates',
    category: 'Reports & Analytics',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: Activity,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Safety Widget
  {
    id: 'safety',
    name: 'Safety Metrics',
    description: 'Safety incidents and inspection status',
    category: 'Reports & Analytics',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: Shield,
    supportsOrgScope: false,
    supportsProjectScope: true,
  },

  // Milestone & Deadline Widget
  {
    id: 'milestones',
    name: 'Upcoming Milestones',
    description: 'Key deadlines and milestone tracking',
    category: 'Tasks & Work',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '2x1',
    icon: Flag,
    supportsOrgScope: true,
    supportsProjectScope: true,
  },

  // Weather Widget
  {
    id: 'weather',
    name: 'Weather',
    description: 'Current weather and forecast for project site',
    category: 'Reports & Analytics',
    sizes: ['1x1', '2x1', '2x2'],
    defaultSize: '1x1',
    icon: Cloud,
    supportsOrgScope: false,
    supportsProjectScope: true,
  },
];

// Helper functions
export const getWidgetComponent = (type: string): React.ComponentType<any> | null => {
  return WIDGET_COMPONENTS[type] || null;
};

export const getWidgetType = (id: string): WidgetType | null => {
  return WIDGET_TYPES.find(type => type.id === id) || null;
};

export const getWidgetsByCategory = (category: string): WidgetType[] => {
  return WIDGET_TYPES.filter(type => type.category === category);
};

export const getWidgetsByScope = (scope: 'org' | 'project'): WidgetType[] => {
  return WIDGET_TYPES.filter(type =>
    scope === 'org' ? type.supportsOrgScope : type.supportsProjectScope
  );
};
